import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { me } from "./api";
import Login from "./pages/Login";
import AppShell from "./layouts/AppShell";
import Dashboard from "./pages/Dashboard";
import Bookings from "./pages/Bookings";
import BookingAccounts from "./pages/BookingAccounts";
import { FreeInvoices, Invoicing, Quotes } from "./pages/Documents";
import Sites from "./pages/Sites";
import Rooms from "./pages/Rooms";
import Services from "./pages/Services";
import Customers from "./pages/Customers";
import Tables from "./pages/Tables";
import System from "./pages/System";
import Company from "./pages/Company";
import { Prints, Profitability, Statistics } from "./pages/UtilityWindows";
import Users from "./pages/Users";
import Roles from "./pages/Roles";
import Search from "./pages/Search";
import Notifications from "./pages/Notifications";
import ResetPassword from "./pages/ResetPassword";
import { canAccessPage, getFirstAllowedRoute, getRoutePageKey, readStoredUser } from "./utils/permissions";
import { initManagementTooltipPortal } from "./utils/managementTooltipPortal";
import "./assets/css/app.css";
import "./assets/css/overlay.css";

function Protected({ children }) {
  const [ok, setOk] = useState(null);
  useEffect(() => {
    const local = localStorage.getItem("user");
    if (!local) return setOk(false);
    (async () => {
      try {
        const data = await me();
        if (data?.user) localStorage.setItem("user", JSON.stringify(data.user));
        setOk(true);
      } catch {
        localStorage.removeItem("user");
        localStorage.removeItem("tenant");
        setOk(false);
      }
    })();
  }, []);
  if (ok === null) return <div className="app_loading">Caricamento...</div>;
  if (!ok) return <Navigate to="/" replace />;
  return children;
}

function PermissionRoute({ pageKey, children }) {
  const user = readStoredUser();
  if (!canAccessPage(user, pageKey, 1)) return <Navigate to={getFirstAllowedRoute(user)} replace />;
  return children;
}

function AppIndexRedirect() {
  return <Navigate to={getFirstAllowedRoute(readStoredUser())} replace />;
}

function AppWildcardRedirect() {
  const location = useLocation();
  const user = readStoredUser();
  const pageKey = getRoutePageKey(location.pathname);
  if (pageKey && canAccessPage(user, pageKey, 1)) return <Navigate to={location.pathname} replace />;
  return <Navigate to={getFirstAllowedRoute(user)} replace />;
}

export default function App() {
  useEffect(() => initManagementTooltipPortal(), []);

  useEffect(() => {
    const removeButtonsFromTabOrder = (root) => {
      const apply = (button) => {
        if (!(button instanceof HTMLButtonElement)) return;
        if (button.classList.contains("vesta_select_trigger")) return;
        button.tabIndex = -1;
      };
      apply(root);
      root?.querySelectorAll?.("button").forEach(apply);
    };
    removeButtonsFromTabOrder(document);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) removeButtonsFromTabOrder(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const toggleOverlayCheckboxOnEnter = (event) => {
      if (event.key !== "Enter" || event.repeat) return;
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || target.type !== "checkbox") return;
      if (!target.closest(".overlay_card") || target.disabled || target.readOnly) return;
      event.preventDefault();
      target.click();
    };

    document.addEventListener("keydown", toggleOverlayCheckboxOnEnter);
    return () => document.removeEventListener("keydown", toggleOverlayCheckboxOnEnter);
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/app" element={<Protected><AppShell /></Protected>}>
          <Route index element={<AppIndexRedirect />} />
          <Route path="dashboard" element={<PermissionRoute pageKey="dashboard"><Dashboard /></PermissionRoute>} />
          <Route path="bookings" element={<PermissionRoute pageKey="booking"><Bookings /></PermissionRoute>} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="booking-accounts" element={<PermissionRoute pageKey="bookingaccount"><BookingAccounts /></PermissionRoute>} />
          <Route path="quotes" element={<PermissionRoute pageKey="quote"><Quotes /></PermissionRoute>} />
          <Route path="free-invoices" element={<PermissionRoute pageKey="freeInvoice"><FreeInvoices /></PermissionRoute>} />
          <Route path="invoicing" element={<PermissionRoute pageKey="invoicing"><Invoicing /></PermissionRoute>} />
          <Route path="search" element={<Search />} />
          <Route path="sites" element={<PermissionRoute pageKey="site"><Sites /></PermissionRoute>} />
          <Route path="rooms" element={<PermissionRoute pageKey="room"><Rooms /></PermissionRoute>} />
          <Route path="services" element={<PermissionRoute pageKey="accommodationService"><Services /></PermissionRoute>} />
          <Route path="customers" element={<PermissionRoute pageKey="customer"><Customers /></PermissionRoute>} />
          <Route path="prints" element={<PermissionRoute pageKey="prints"><Prints /></PermissionRoute>} />
          <Route path="statistics" element={<PermissionRoute pageKey="statistic"><Statistics /></PermissionRoute>} />
          <Route path="profitability" element={<PermissionRoute pageKey="profitability"><Profitability /></PermissionRoute>} />
          <Route path="tables" element={<PermissionRoute pageKey="table"><Tables /></PermissionRoute>} />
          <Route path="system" element={<PermissionRoute pageKey="system"><System /></PermissionRoute>} />
          <Route path="company" element={<PermissionRoute pageKey="company"><Company /></PermissionRoute>} />
          <Route path="users" element={<PermissionRoute pageKey="user"><Users /></PermissionRoute>} />
          <Route path="roles" element={<PermissionRoute pageKey="role"><Roles /></PermissionRoute>} />
          <Route path="*" element={<AppWildcardRedirect />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
